import React, { CSSProperties, useCallback, useEffect, useRef } from 'react'
import Button2 from '@embracesbs/component-button2/Button2'
import Text from '@embracesbs/component-text'
import { Tenant, TenantConfigDisplayValue } from '../../Interfaces/Types'
import Icon from '@embracesbs/component-icon'
import { clientSecretExpiryStatus, clientSecretExpiryText, environmentLetters, itemColor } from '../../Utilities/helpers/helpers'
import { useTenants } from '../Context/TenantContext'
import SupportLoginQuickButton from '../Auth/SupportLoginQuickButton'
import { useLocation, useNavigate } from 'react-router-dom'
import QuickLaunchButton from './QuickLaunchButton'
import { useMediaQuery } from '../../Hooks'

interface TenantListItemProps {
  tenant: Tenant
  // The configuration values and Terraform module versions the search filtered on,
  // empty for a search that filtered on neither (WI 159594).
  configValues?: TenantConfigDisplayValue[]
}

const TenantListItem: React.FC<TenantListItemProps> = ({ tenant, configValues }) => {
  const { selectedTenant, setSelectedTenantId } = useTenants()
  const history = useNavigate()
  const location = useLocation()
  // On narrow screens the Select/Close buttons shrink to icon-only.
  const isCompact = useMediaQuery('(max-width: 1400px)')

  const handleSelect = useCallback(
    (subscriptionId: string, tenantId: string) => {
      // Coming from another page (e.g. advanced search): push, so the browser
      // back button returns to the search results. On the dashboard itself:
      // replace, so switching tenants doesn't spam the history.
      const replace = !location.pathname.startsWith('/search')
      history('/subscriptions/' + subscriptionId + '/tenant/' + tenantId, { replace })
    },
    [history, location.pathname]
  )

  const isActive = tenant.id === selectedTenant?.id
  const statusColor = itemColor(tenant, false)
  const secretExpiry = clientSecretExpiryStatus(tenant.clientSecretExpiresOnUtc)
  const showSecretExpiry = secretExpiry.state === 'expiring' || secretExpiry.state === 'expired'
  const cardRef = useRef<HTMLDivElement | null>(null)

  // Scroll the selected tenant into view — covers deep links
  // (/subscriptions/:sub/tenant/:id) and navigating in from advanced search,
  // where the list renders after the selection is already set.
  // `block: 'nearest'` keeps the scroll inside the list's own scroll area.
  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isActive])

  // Selected card gets a full outline; the 1px borders are always present
  // (transparent when inactive) so selecting doesn't shift the layout.
  const cardStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'stretch',
    borderStyle: 'solid',
    // Per-side longhands only: mixing the `borderColor` shorthand with
    // `borderLeftColor` makes React updates overwrite the left accent.
    borderTopColor: isActive ? statusColor : 'transparent',
    borderRightColor: isActive ? statusColor : 'transparent',
    borderBottomColor: isActive ? statusColor : 'transparent',
    borderLeftColor: statusColor,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '3px',
    borderRadius: '6px',
    overflow: 'hidden',
    background: 'var(--tma-panel-bg)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
    marginBottom: '6px',
    transition: 'border-color 0.2s ease'
  }

  // Full-height status block flush against the left border (no white gap around it).
  const statusBlockStyle: CSSProperties = {
    width: '66px',
    flexShrink: 0,
    alignSelf: 'stretch',
    // statusColor is a var() token — mix, don't append a hex alpha suffix.
    background: `color-mix(in srgb, ${statusColor} 13%, transparent)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const contentStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px'
  }

  return (
    <div ref={cardRef} style={cardStyle}>
      <div style={statusBlockStyle}>
        <Text textStyle='h6' content={environmentLetters(tenant)} style={{ color: statusColor, fontSize: '14px' }} />
      </div>
      <div style={contentStyle}>
        <div style={{ minWidth: 0 }}>
          <Text textStyle='body' content={tenant.tenantName} singleLine hasTooltipEllipsis />
          <Text textStyle='description' textColor='grey' content={tenant.deployStatus} />
          {showSecretExpiry && (
            <Text
              textStyle='description'
              content={clientSecretExpiryText(secretExpiry) ?? ''}
              style={{ color: 'var(--tma-status-failed)' }}
              singleLine
              hasTooltipEllipsis
            />
          )}
          {configValues && configValues.length > 0 && (
            <Text
              textStyle='description'
              textColor='grey'
              content={configValues.map((entry) => `${entry.label}: ${entry.value ?? 'not set'}`).join(' · ')}
              singleLine
              hasTooltipEllipsis
            />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <QuickLaunchButton tenant={tenant} />
          <SupportLoginQuickButton tenant={tenant} />
          <Button2
            buttonSize='30px'
            style={{ minWidth: isCompact ? undefined : '92px', borderRadius: '8px', justifyContent: 'center' }}
            leftElement={<Icon color={isActive ? '#ffffff' : '#416eec'} iconName={isActive ? 'ArrowUp1' : 'ArrowRight1'} size={12} />}
            tabIndex={-1}
            text={isCompact ? '' : isActive ? 'Close' : 'Select'}
            aria-label={isActive ? 'Close' : 'Select'}
            type='button'
            onClick={() => (isActive ? setSelectedTenantId(null) : handleSelect(tenant.subscriptionId, tenant.id))}
            variant={isActive ? 'contained' : 'outlined'}
          />
        </div>
      </div>
    </div>
  )
}

export default TenantListItem
